/**
 * QAFindingResponseDialog — Jun 2026
 * Opened from the notification bell ("Open QA Finding"). Shows the signed
 * QA findings to the audited provider so they can respond once fixed, and
 * lets the auditor verify a response (close or reopen the finding).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  open: { label: 'Open', cls: 'bg-amber-500 text-white' },
  provider_responded: { label: 'Awaiting Verification', cls: 'bg-blue-500 text-white' },
  closed: { label: 'Closed', cls: 'bg-emerald-500 text-white' },
};

export const QAFindingResponseDialog = ({ open, onOpenChange, reviewId, sectionId }) => {
  const [finding, setFinding] = useState(null);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!reviewId || !sectionId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/qa-audit/findings/${reviewId}/${sectionId}`);
      setFinding(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load QA finding');
    } finally {
      setLoading(false);
    }
  }, [reviewId, sectionId]);

  useEffect(() => { if (open) { setNote(''); load(); } }, [open, load]);

  const respond = async () => {
    if (!note.trim()) { toast.error('Please describe what was fixed'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/qa-audit/findings/${reviewId}/${sectionId}/respond`, { response_note: note });
      toast.success('Response sent — the auditor will verify');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send response');
    } finally { setBusy(false); }
  };

  const resolve = async (action) => {
    setBusy(true);
    try {
      await axios.post(`${API}/qa-audit/findings/${reviewId}/${sectionId}/resolve`, { action, note });
      toast.success(action === 'close' ? 'Finding closed' : 'Finding reopened — provider notified');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update finding');
    } finally { setBusy(false); }
  };

  const sec = finding?.section;
  const badge = STATUS_BADGE[finding?.resolution_status] || null;
  const needsWork = (sec?.checklist_items || []).filter((i) => i.status === 'needs_work');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="qa-finding-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            QA Finding — {finding?.review_type_label || ''}
            {badge && <Badge className={`${badge.cls} text-xs`}>{badge.label}</Badge>}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-slate-500 py-6 text-center">Loading…</p>
        ) : !finding ? (
          <p className="text-sm text-slate-500 py-6 text-center">Finding not found</p>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
            <div className="text-xs text-slate-500">
              Audited provider: <span className="font-medium text-slate-700">{finding.provider_name || '—'}</span>
              {' · '}Auditor: <span className="font-medium text-slate-700">{finding.auditor_name || '—'}</span>
            </div>
            {needsWork.length > 0 && (
              <div>
                <Label className="text-xs text-slate-500">Items flagged as Needs Work</Label>
                <ul className="mt-1 space-y-1">
                  {needsWork.map((i, idx) => (
                    <li key={idx} className="text-sm bg-red-50 border border-red-200 rounded px-2 py-1">
                      <span className="font-medium">{i.item_name}</span>
                      {i.notes ? <span className="text-slate-600"> — {i.notes}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div>
              <Label className="text-xs text-slate-500">Findings</Label>
              <p className="text-sm mt-1 whitespace-pre-wrap">{sec?.findings || '—'}</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Recommendations</Label>
              <p className="text-sm mt-1 whitespace-pre-wrap">{sec?.recommendations || '—'}</p>
            </div>
            {sec?.reopen_note && finding.resolution_status === 'open' && (
              <div className="p-2 bg-amber-50 border border-amber-200 rounded text-sm">
                <span className="font-medium">Reopened:</span> {sec.reopen_note}
              </div>
            )}
            {sec?.provider_response && (
              <div className="p-2 bg-blue-50 border border-blue-200 rounded text-sm" data-testid="qa-finding-provider-response">
                <span className="font-medium">{sec.provider_responded_by_name || 'Provider'} responded:</span> {sec.provider_response}
                {sec.provider_responded_at && (
                  <span className="text-xs text-slate-500 block mt-0.5">{new Date(sec.provider_responded_at).toLocaleString()}</span>
                )}
              </div>
            )}
            {finding.resolution_status === 'closed' && (
              <div className="p-2 bg-emerald-50 border border-emerald-200 rounded text-sm">
                <span className="font-medium">Closed by {sec.resolved_by_name}</span>
                {sec.resolution_note ? ` — ${sec.resolution_note}` : ''}
              </div>
            )}
            {(finding.can_respond || finding.can_resolve) && (
              <div className="pt-2 border-t space-y-2">
                <Label className="text-xs text-slate-500">
                  {finding.can_respond ? 'Describe what was fixed (required)' : 'Verification note (optional)'}
                </Label>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder={finding.can_respond ? 'e.g., Late notes were completed and signed on 6/20…' : 'e.g., Verified all notes are now signed'}
                  data-testid="qa-finding-note-input"
                />
                <div className="flex gap-2">
                  {finding.can_respond && (
                    <Button onClick={respond} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="qa-finding-respond-btn">
                      Mark as Fixed
                    </Button>
                  )}
                  {finding.can_resolve && (
                    <>
                      <Button onClick={() => resolve('close')} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="qa-finding-close-btn">
                        Verify & Close
                      </Button>
                      <Button onClick={() => resolve('reopen')} disabled={busy} variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50" data-testid="qa-finding-reopen-btn">
                        Reopen
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
